import { useLang } from 'hooks/lang';

import { actions, issueStatuses, issueStatusesByCode, FullProjectIssue } from 'entities';

import { Column, Row, Button, Box, Card } from 'components/Blocks';
import { CircleCheckIcon, DoorClosedIcon, XIcon } from 'components/Icons';

import { useConfirmation } from 'providers/Confirmation';
import { useProjectUpdate } from 'providers/Project';

import { BaseProps } from './types';

export function FinishIssue({ issue, updateIssue, setTab }: BaseProps) {
  const { t } = useLang();

  const { requestConfirmation } = useConfirmation();

  const updateProject = useProjectUpdate();

  const changeIssueStatus = actions.projects.changeIssueStatus.use();

  const changeStatus = (status: string): void => {
    requestConfirmation({
      title: t('mark-issue-as', { status: issueStatusesByCode[status].getLabel(t) }) as string,
      text: t('if-issue-is-still-present-refrain-from-doing-it'),
      type: 'irreversible',
      onConfirm: async () => {
        const success = await changeIssueStatus.execute({
          issue: issue!.id,
          project: issue!.project,
          status,
        });

        if (!success) return;

        updateIssue({
          ...issue,
          status,
        } as FullProjectIssue);

        updateProject(issue.project, (old) => ({
          ...old,
          activeIssues: Math.max(0, (old.activeIssues as number) - 1),
        }));

        setTab('main');
      },
    });
  };

  return (
    <Card flow="column">
      <Column gap="2" alignItems="center">
        <Box border="circle" className="bg-muted p-3">
          <DoorClosedIcon color="primary" size={24} />
        </Box>

        <h6>{t('finish-this-issue?')}</h6>
      </Column>

      <Row justifyContent="center" className="phone:flex-col">
        <Button
          className="phone:w-full"
          variant="outlined"
          onClick={() => changeStatus(issueStatuses.closed.code)}
          color="destructive"
        >
          <XIcon />
          {t('close-issue')}
        </Button>

        <Button
          className="phone:w-full"
          variant="outlined"
          onClick={() => changeStatus(issueStatuses.resolved.code)}
          color="success"
        >
          <CircleCheckIcon />
          {t('mark-as-resolved')}
        </Button>
      </Row>
    </Card>
  );
}
